import {
  getSalesAnalysisDataSource,
  getSalesAnalysisInvoiceFilters,
  getSalesAnalysisInvoicesIsOverriding,
  getSalesAnalysisInvoicesLayout,
  getSalesAnalysisInvoicesTotal,
  getSalesAnalysisQuoteFilters,
  getSalesAnalysisQuotesIsOverriding,
  getSalesAnalysisQuotesLayout,
  getSalesAnalysisQuotesTotal,
} from 'selectors/salesContext'
import {
  getFiltersFromLayout,
  getLayoutTypeLabel,
  getSalesAnalysisPivotTableParamsFromLayout,
  getSelectedLayout,
  layoutTypes,
} from 'helpers/layouts'
import {getGlobalLayouts, getLayouts} from 'selectors/data'
import {salesContextSalesAnalysisQuotesViewPivotTableKey} from 'reducers/salesContext/salesAnalysisView/quoteDataSource'
import {salesContextSalesAnalysisInvoicesViewPivotTableKey} from 'reducers/salesContext/salesAnalysisView/invoiceDataSource'
import {
  updateSalesAnalysisInvoicingFilters,
  updateSalesAnalysisInvoicingIsOverriding,
  updateSalesAnalysisQuotingFilters,
  updateSalesAnalysisQuotingIsOverriding,
} from 'actions/sales'
import {InvoiceStatus} from 'helpers/invoices'
import {getQuoteTypes, QuoteStatus} from 'helpers/quotes'
import {isPartDeliveredOptionDisabled} from 'helpers/deliveries'
import {
  getGroups,
  getSearch,
  getSelectedColumns as getSelectedPivotTableColumns,
  getSortColumn,
  getSortDirection,
} from 'selectors/pivotTable'
import {getOrderLayoutParams} from 'helpers/sales/salesAnalysisOrders'
import {getInvoiceLayoutParams} from 'helpers/sales/salesAnalysisInvoices'
import {get, isEmpty, isNil} from 'lodash'
import {getContactName, isDormantContact, sortContacts, TYPE} from 'helpers/contacts'
import {
  CONTACT_PREFIX,
  findTeamMember,
  getAllTeamsForTeamId,
  getTeamMembersForTeams,
  getTeamToDisplayForTeamMember,
  isWholeCompanyManager,
  mapTeamMembersToContacts,
  sortTeams,
  TEAM_PREFIX,
} from 'helpers/teams'

const getInvoiceStatusOptions = () => {
  return [
    {
      value: InvoiceStatus.ALL_INVOICES.id,
      label: 'Invoice',
    },
    {
      value: InvoiceStatus.ALL_CREDIT_NOTES.id,
      label: 'Credit Note',
    },
  ]
}

const getQuoteStatusOptions = (user) => {
  const statusesToExclude = [
    QuoteStatus.allQuotes,
    QuoteStatus.activeQuotes,
    QuoteStatus.recentlyOpened,
    QuoteStatus.unsent,
    QuoteStatus.unconfirmed,
    QuoteStatus.requiredSoon,
    QuoteStatus.expiring,
    QuoteStatus.expired,
    QuoteStatus.notAccepted,
    QuoteStatus.inProduction,
    QuoteStatus.inDelivery,
  ]

  return getQuoteTypes()
      .filter(quoteType => !statusesToExclude.includes(quoteType.id))
      .map(type => ({
        value: type.id,
        label: type.title,
        disabled: isPartDeliveredOptionDisabled(type.id, user),
      }))
}

export const getDataSources = () => {
  return [
    layoutTypes.ORDERS,
    layoutTypes.INVOICES,
  ].map(layoutType => {
    return {
      id: layoutType,
      title: getLayoutTypeLabel(layoutType),
    }
  })
}

export const isOrdersLayoutType = dataSource => {
  return dataSource === layoutTypes.ORDERS
}

export const getLayoutId = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuotesLayout(state)
      : getSalesAnalysisInvoicesLayout(state)
}

export const getDataSourceLayout = state => {
  const layoutId = getLayoutId(state)
  const globalLayouts = getGlobalLayouts(state)
  const layouts = getLayouts(state)
  const dataSource = getSalesAnalysisDataSource(state)

  return getSelectedLayout(layoutId, [...globalLayouts, ...layouts], dataSource)
}

export const getSalesAnalysisLayoutFilters = state => {
  const layout = getDataSourceLayout(state)
  const dataSource = getSalesAnalysisDataSource(state)

  return getFiltersFromLayout({layout, dataSource})
}

export const getPivotTableSettingsForDataSource = state => {
  const layout = getDataSourceLayout(state)

  return getSalesAnalysisPivotTableParamsFromLayout(layout)
}

export const getIsOverridingForDataSource = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuotesIsOverriding(state)
      : getSalesAnalysisInvoicesIsOverriding(state)
}

export const getSalesAnalysisDataSourceFilters = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuoteFilters(state)
      : getSalesAnalysisInvoiceFilters(state)
}

export const getFiltersForDataSource = state => {
  const isOverriding = getIsOverridingForDataSource(state)

  return isOverriding ? getSalesAnalysisDataSourceFilters(state) : getSalesAnalysisLayoutFilters(state)
}

export const getPivotTableKey = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource)
      ? salesContextSalesAnalysisQuotesViewPivotTableKey
      : salesContextSalesAnalysisInvoicesViewPivotTableKey
}

export const getSalesAnalysisDataSourceTotal = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuotesTotal(state)
      : getSalesAnalysisInvoicesTotal(state)
}
export const getDataSourcePivotTableLabel = state => {
  const dataSource = getSalesAnalysisDataSource(state)

  return isOrdersLayoutType(dataSource) ? 'Quote' : 'Invoice'
}

export const updateSalesAnalysisDataSourceFilters = (filters, dataSource) => {
  return isOrdersLayoutType(dataSource)
      ? updateSalesAnalysisQuotingFilters(filters)
      : updateSalesAnalysisInvoicingFilters(filters)
}

export const updateSalesAnalysisDataSourceIsOverriding = (isOverriding, dataSource) => {
  return isOrdersLayoutType(dataSource)
      ? updateSalesAnalysisQuotingIsOverriding(isOverriding)
      : updateSalesAnalysisInvoicingIsOverriding(isOverriding)
}

export const getDataSourceStatusOptions = (dataSource, user) => {
  return isOrdersLayoutType(dataSource)
      ? getQuoteStatusOptions(user)
      : getInvoiceStatusOptions()
}

export const getLayoutParams = state => {
  const filters = getSalesAnalysisDataSourceFilters(state)
  const pivotTableKey = getPivotTableKey(state)
  const dataSource = getSalesAnalysisDataSource(state)

  const pivotTableSettings = {
    groups: getGroups(state, pivotTableKey),
    search: getSearch(state, pivotTableKey),
    sortColumn: getSortColumn(state, pivotTableKey),
    sortDirection: getSortDirection(state, pivotTableKey),
    selectedFields: getSelectedPivotTableColumns(state, pivotTableKey),
  }

  const params = {
    ...filters,
    ...pivotTableSettings,
  }

  return isOrdersLayoutType(dataSource)
      ? getOrderLayoutParams(params)
      : getInvoiceLayoutParams(params)
}

export const getSalesAnalysisUsersFilterPlaceholder = (teamUserOptions) => {
  return !isEmpty(teamUserOptions)
      ? teamUserOptions[0].label
      : 'Whole Company'
}

export const getSalesAnalysisUsersFilterOptions = (
    user,
    teams,
    teamMembers,
    homeContacts,
    pluralNoun
) => {
  const options = []
  const teamMember = findTeamMember(user.contactId, teamMembers)
  const teamId = getTeamToDisplayForTeamMember(teamMember)
  const myOption = {
    value: `${CONTACT_PREFIX}${user.contactId}`,
    label: `My ${pluralNoun}`,
  }

  if (!isNil(teamId)) {
    options.push({
      value: `${TEAM_PREFIX}${teamId}`,
      label: isWholeCompanyManager(teamId) ? 'Whole Company' : 'My Team',
    })

    const myTeams = getAllTeamsForTeamId(teamId, teams)
    const filteredTeams = myTeams.filter(team => team.id !== teamId)
    const sortedTeams = sortTeams(filteredTeams)
    const sortedTeamContacts = getContactsForUsersFilter(user, teamId, homeContacts, teamMembers, myTeams)

    options.push(...[
      ...sortedTeams.map(team => {
        return {
          value: `${TEAM_PREFIX}${team.id}`,
          label: team.name,
        }
      }),
      myOption,
      ...sortedTeamContacts.map(contact => {
        return {
          value: `${CONTACT_PREFIX}${contact.id}`,
          label: getContactName(contact),
          isDormant: isDormantContact(get(contact, 'type', TYPE.general)),
        }
      }),
    ])
  } else {
    options.push(myOption)
  }

  return options
}

export const getContactsForUsersFilter = (user, teamId, homeContacts, teamMembers, myTeams) => {
  if (isWholeCompanyManager(teamId)) {
    const filteredContacts = homeContacts.filter(contact => contact.id !== user.contactId)

    return sortContacts(filteredContacts)
  } else {
    const myTeamMembers = getTeamMembersForTeams(myTeams, teamMembers)
    const myTeamContacts = mapTeamMembersToContacts(myTeamMembers, homeContacts)
    const filteredTeamContacts = myTeamContacts.filter(contact => contact.id !== user.contactId)

    return sortContacts(filteredTeamContacts)
  }
}

export const  getUpdatedContacts  = (teamUserOptions, includeDormants, contacts) => {
  const dormantContactIds = teamUserOptions
      .filter(teamUserOption => teamUserOption.isDormant === true)
      .map(teamOrUserOption => teamOrUserOption.value)

  return includeDormants === false
      ? contacts.filter(contact => !dormantContactIds.includes(contact))
      : contacts
}
